import React, { useState } from 'react';
import MoreVertical from 'lucide-react/dist/esm/icons/more-vertical';
import Download from 'lucide-react/dist/esm/icons/download';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import HardDrive from 'lucide-react/dist/esm/icons/hard-drive';
import { MyFile } from '../../lib/myFilesService';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

interface FileListProps {
  files: MyFile[];
  onFileClick: (file: MyFile) => void;
  onFileDelete: (fileId: string) => Promise<void>;
  onFileDownload: (fileId: string) => Promise<void>;
  formatFileSize: (bytes: number) => string;
  getFileIcon: (fileType: string) => string;
}

export function FileList({
  files,
  onFileClick,
  onFileDelete,
  onFileDownload,
  formatFileSize,
  getFileIcon
}: FileListProps) {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleDownload = async (fileId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setDownloading(fileId);
      await onFileDownload(fileId);
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (fileId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      return;
    }
    
    try {
      setDeleting(fileId);
      await onFileDelete(fileId);
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      custom: '#abd3d2',
      legal: '#172241',
      financial: '#4ade80',
      health: '#ef4444',
      pets: '#f97316',
      travel: '#3b82f6',
      digital: '#8b5cf6',
      legacy: '#facc15',
      ndis: '#06b6d4'
    };
    return colors[category] || '#abd3d2';
  };

  return (
    <div className="space-y-2">
      {files.map((file) => (
        <Card
          key={file.id}
          className="p-4 cursor-pointer hover:shadow-md transition-shadow group"
          onClick={() => onFileClick(file)}
        >
          <div className="flex items-center gap-4">
            {/* File Icon */}
            <div className="text-2xl flex-shrink-0">
              {getFileIcon(file.mime_type || file.file_type || '')}
            </div>

            {/* File Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h4 className="font-medium text-gray-900 truncate" title={file.title}>
                  {file.title}
                </h4>
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getCategoryColor(file.category) }}
                />
                <span className="text-xs text-gray-500 capitalize flex-shrink-0">{file.category}</span>
              </div>
              
              <p className="text-sm text-gray-500 truncate mb-2" title={file.file_name}>
                {file.file_name}
              </p>
              
              {file.description && (
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                  {file.description}
                </p>
              )}

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <HardDrive size={12} />
                  {formatFileSize(file.file_size)}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  {formatDate(file.created_at)}
                </span>
                {file.tags && file.tags.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {file.tags.length} tag{file.tags.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {/* Tags */}
              {file.tags && file.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {file.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-idecide-primary bg-opacity-20 text-idecide-dark text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                  {file.tags.length > 3 && (
                    <span className="text-xs text-gray-500">+{file.tags.length - 3}</span>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => handleDownload(file.id, e)}
                disabled={downloading === file.id}
                className="h-8 w-8 p-0"
              >
                <Download size={16} />
              </Button>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => handleDelete(file.id, e)}
                disabled={deleting === file.id}
                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
} 